// libraries
import Image from 'next/image';
// utils
import { trackEvent } from '../../../utils/FunctionsGlobals';
import type { HomeProprietariosConfig, ProprietarioCard } from '../../../utils/homeProprietarios';
import styles from './ProprietariosPieces.module.css';

// A seção usa a MESMA fonte do site (`--fonte-principal`; no cs2451 = Roboto Condensed),
// pedido do Daniel em 11/09 — antes os títulos saíam na fonte do mockup do cliente.
// Nenhuma fonte é declarada aqui: o h2/h3 herda a família do tenant pelo CSS global, então
// a seção acompanha sozinha quem trocar de fonte (ex.: a Nunito Sans do cs2631).

interface IProprietariosPieces {
    site: any;
    config: HomeProprietariosConfig;
}

const ProprietariosPieces: React.FC<IProprietariosPieces> = ({ site, config }) => {
    // Verde institucional do tenant (cs2451: cor1 = #062f2e). Ler da paleta faz a
    // seção acompanhar uma troca de marca sem precisar de novo deploy dos 300 sites.
    const verde = site?.cor1 || '#062f2e';

    const handleCardClick = (card: ProprietarioCard) => {
        // O JS de marketing (GTM/GA4/Pixel) é ADIADO até a 1ª interação ou 8s
        // (`pages/_app.tsx`, marketingReady). Entre o pointerdown e o click pode não
        // haver `window.dataLayer` ainda — e aí o trackEvent cairia no fallback
        // `gtag`, que também não existe, perdendo o evento. Semear o array enfileira
        // o push; o snippet do GTM faz `dataLayer = dataLayer || []` e consome a fila
        // ao carregar. Local, sem tocar no trackEvent compartilhado.
        if (typeof window !== 'undefined') {
            (window as any).dataLayer = (window as any).dataLayer || [];
        }
        trackEvent('home_proprietario_click', {
            section: 'home_proprietarios',
            card_id: card.id,
            card_title: card.titulo,
            cta_label: card.cta,
            destination_url: card.href,
            codsite: Number(site?.id) || undefined,
        });
    };

    return (
        <section
            id="proprietarios"
            className={styles.section}
            aria-labelledby="proprietarios-titulo"
            style={{ ['--hp-green' as any]: verde }}
        >
            <div className={styles.header}>
                <p className={styles.eyebrow}>{config.eyebrow}</p>
                <h2 id="proprietarios-titulo" className={styles.title}>
                    {config.titulo}
                </h2>
                <p className={styles.subtitle}>{config.apoio}</p>
            </div>

            <div className={styles.grid}>
                {config.cards.map((card) => (
                    <article key={card.id} className={styles.cardWrap}>
                        {/* UMA única âncora por card: o card inteiro é o link e o "botão" é um
                            <span> estilizado (mesmo padrão do VenturesHighlight). Isso evita
                            <a> dentro de <a> e garante UM disparo por clique — clicar no botão
                            borbulha para este mesmo onClick, sem stopPropagation nem overlay. */}
                        <a
                            id={card.id}
                            data-track-id={card.id}
                            className={styles.card}
                            href={card.href}
                            onClick={() => handleCardClick(card)}
                            aria-label={`${card.titulo} — ${card.cta}`}
                            rel="noopener"
                            data-no-quicklink
                            data-no-instant
                            data-no-prefetch
                        >
                            <Image
                                className={styles.photo}
                                src={card.img}
                                alt={card.alt}
                                width={1200}
                                height={800}
                                sizes="(max-width: 820px) 92vw, 400px"
                                loading="lazy"
                                decoding="async"
                            />
                            <div className={styles.body}>
                                <h3 className={styles.cardTitle}>{card.titulo}</h3>
                                <p className={styles.cardText}>{card.texto}</p>
                                <span className={styles.cta}>{card.cta}</span>
                            </div>
                        </a>
                    </article>
                ))}
            </div>
        </section>
    );
};

export default ProprietariosPieces;
